import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast as sonnerToast } from 'sonner';
import { isOpsItemType, opsCreatePath } from '../../lib/ops-record';
import { SERVICE_REQUEST_CREATE_PATH } from '../../lib/service-requests';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

type ConfirmOpts = {
  title: string;
  description: string;
  confirmLabel?: string;
  destructive?: boolean;
};

/** A task created from an initiative's launch plan: any stream, linked, with a due date. */
export type LaunchStepDraft = {
  initiativeId: string;
};

/** What the shared create screen collected, before it is saved. */
export type CreateTaskDraft = {
  title: string;
  projectId: string;
  ownerIds: string[];
  dueDate: string;
  description: string;
};

type CreateWorkItemState = {
  open: boolean;
  projectId?: string;
  stateId?: string;
  parentId?: string;
  type?: 'task' | 'incident' | 'problem' | 'initiative';
  launchStep?: LaunchStepDraft;
  /** Saves the same screen somewhere other than a new stream task, such as a department step. */
  capture?: (draft: CreateTaskDraft) => Promise<void>;
};

type OverlayContextValue = {
  toast: (message: string) => void;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  openCreateWorkItem: (opts?: {
    projectId?: string;
    stateId?: string;
    parentId?: string;
    type?: 'task' | 'incident' | 'problem' | 'service_request' | 'initiative';
    launchStep?: LaunchStepDraft;
    capture?: (draft: CreateTaskDraft) => Promise<void>;
  }) => void;
  openCreateProject: () => void;
  openCreateView: (opts?: { projectId?: string }) => void;
  openPowerK: () => void;
  openShortcuts: () => void;
  createWorkItem: CreateWorkItemState;
  createProjectOpen: boolean;
  createView: { open: boolean; projectId?: string };
  powerKOpen: boolean;
  shortcutsOpen: boolean;
  setCreateWorkItemOpen: (open: boolean) => void;
  setCreateProjectOpen: (open: boolean) => void;
  setCreateViewOpen: (open: boolean) => void;
  setPowerKOpen: (open: boolean) => void;
  setShortcutsOpen: (open: boolean) => void;
  blockingCount: number;
  pushBlock: () => void;
  popBlock: () => void;
  isBlocked: boolean;
};

const OverlayContext = createContext<OverlayContextValue | null>(null);

export function useOverlays(): OverlayContextValue {
  const ctx = useContext(OverlayContext);
  if (!ctx) {
    throw new Error('useOverlays must be used within OverlayProvider');
  }
  return ctx;
}

export function OverlayProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmView, setConfirmView] = useState<ConfirmOpts | null>(null);
  const confirmResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const confirmSettledRef = useRef(false);
  const [createWorkItem, setCreateWorkItem] = useState<CreateWorkItemState>({ open: false });
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createView, setCreateView] = useState<{ open: boolean; projectId?: string }>({
    open: false,
  });
  const [powerKOpen, setPowerKOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [blockingCount, setBlockingCount] = useState(0);

  const toast = useCallback((message: string) => {
    sonnerToast(message);
  }, []);

  const confirm = useCallback((opts: ConfirmOpts) => {
    return new Promise<boolean>((resolve) => {
      confirmSettledRef.current = false;
      confirmResolveRef.current = resolve;
      setConfirmView(opts);
      setConfirmOpen(true);
    });
  }, []);

  const settleConfirm = useCallback((ok: boolean) => {
    if (confirmSettledRef.current) return;
    confirmSettledRef.current = true;
    const resolve = confirmResolveRef.current;
    confirmResolveRef.current = null;
    setConfirmOpen(false);
    resolve?.(ok);
  }, []);

  useEffect(() => {
    if (confirmOpen || !confirmView) return;
    const timer = window.setTimeout(() => setConfirmView(null), 300);
    return () => window.clearTimeout(timer);
  }, [confirmOpen, confirmView]);

  const pushBlock = useCallback(() => setBlockingCount((n) => n + 1), []);
  const popBlock = useCallback(() => setBlockingCount((n) => Math.max(0, n - 1)), []);

  const value = useMemo<OverlayContextValue>(
    () => ({
      toast,
      confirm,
      openCreateWorkItem: (opts) => {
        if (opts?.type === 'service_request') {
          setCreateWorkItem((current) => ({ ...current, open: false }));
          navigate(SERVICE_REQUEST_CREATE_PATH);
          return;
        }
        if (opts?.type && isOpsItemType(opts.type)) {
          setCreateWorkItem((current) => ({ ...current, open: false }));
          navigate(opsCreatePath(opts.type, { projectId: opts.projectId, stateId: opts.stateId }));
          return;
        }
        setCreateWorkItem({
          open: true,
          projectId: opts?.projectId,
          stateId: opts?.stateId,
          parentId: opts?.parentId,
          type: opts?.launchStep ? 'task' : opts?.type,
          launchStep: opts?.launchStep,
          capture: opts?.capture,
        });
      },
      openCreateProject: () => setCreateProjectOpen(true),
      openCreateView: (opts) => setCreateView({ open: true, projectId: opts?.projectId }),
      openPowerK: () => setPowerKOpen(true),
      openShortcuts: () => setShortcutsOpen(true),
      createWorkItem,
      createProjectOpen,
      createView,
      powerKOpen,
      shortcutsOpen,
      setCreateWorkItemOpen: (open) => setCreateWorkItem((c) => ({ ...c, open })),
      setCreateProjectOpen,
      setCreateViewOpen: (open) => setCreateView((c) => ({ ...c, open })),
      setPowerKOpen,
      setShortcutsOpen,
      blockingCount,
      pushBlock,
      popBlock,
      isBlocked: blockingCount > 0 || createWorkItem.open || createProjectOpen || createView.open
        || powerKOpen || shortcutsOpen || confirmOpen,
    }),
    [
      toast,
      confirm,
      navigate,
      createWorkItem,
      createProjectOpen,
      createView,
      powerKOpen,
      shortcutsOpen,
      blockingCount,
      pushBlock,
      popBlock,
      confirmOpen,
    ],
  );

  return (
    <OverlayContext.Provider value={value}>
      {children}
      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) settleConfirm(false);
        }}
      >
        {confirmView ? (
          <DialogContent onCloseAutoFocus={(event) => event.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{confirmView.title}</DialogTitle>
              <DialogDescription>{confirmView.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => settleConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={confirmView.destructive ? 'destructive' : 'default'}
                onClick={() => settleConfirm(true)}
              >
                {confirmView.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </OverlayContext.Provider>
  );
}
